import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useParams } from 'react-router-dom';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  age,
  CheckIcon,
  githubUrl,
  PHASE_COLOR,
  REVIEW_ENV_REPOSITORY_ANNOTATION,
  usePolledReviewEnvs,
} from './common';
import { ReviewEnvDetail } from './types';

type Row<K extends keyof ReviewEnvDetail> =
  ReviewEnvDetail[K] extends (infer T)[] ? T : never;

const DENSE = {
  paging: false,
  search: false,
  toolbar: false,
  padding: 'dense' as const,
};

function ExternalLink({ href, children }: { href?: string; children: string }) {
  if (!href) return null;
  return (
    <MuiLink href={href} target="_blank" rel="noopener" sx={{ mr: 1.5 }}>
      {children}
    </MuiLink>
  );
}

function conditionStatus(status: string): 'ok' | 'pending' | 'failed' {
  if (status === 'True') return 'ok';
  if (status === 'False') return 'failed';
  return 'pending';
}

function Header({ detail }: { detail: ReviewEnvDetail }) {
  const { entity } = useEntity();
  const env = detail.summary;
  const slug = entity.metadata.annotations?.['github.com/project-slug'];
  const prUrl = env.pr ? githubUrl(slug, env.repository, env.pr) : undefined;
  const now = Date.now();

  const meta: [string, string | undefined][] = [
    ['Kind', env.kind],
    ['Namespace', env.namespace],
    ['Author', env.author],
    ['Image', env.tag],
    ['Age', age(env.createdAt, now)],
    [
      'Deployments',
      env.workloads
        ? `${env.workloads.ready}/${env.workloads.total} available`
        : undefined,
    ],
  ];

  const buttons: { title: string; url?: string }[] = [
    { title: 'Open env', url: env.phase === 'orphaned' ? undefined : env.url },
    { title: 'Pull request', url: prUrl },
    { title: 'PR checks', url: prUrl && `${prUrl}/checks` },
    ...detail.links,
  ];

  return (
    <InfoCard>
      <Box sx={{ mb: 1 }}>
        <Link to="..">
          <Box component="span" sx={{ display: 'inline-flex', gap: 0.5 }}>
            <ArrowBackIcon fontSize="small" /> All review envs
          </Box>
        </Link>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Typography variant="h5">
          {env.pr ? `PR ${env.pr}` : env.name}
        </Typography>
        <Chip
          label={env.phase}
          color={PHASE_COLOR[env.phase]}
          variant={env.phase === 'orphaned' ? 'outlined' : 'filled'}
        />
        <Typography variant="body2" color="text.secondary">
          {env.name}
        </Typography>
      </Box>
      {env.message && (
        <Typography
          variant="body1"
          sx={{ mb: 1.5, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
        >
          {env.message}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
        {meta
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <Box key={k}>
              <Typography variant="caption" color="text.secondary">
                {k}
              </Typography>
              <Typography variant="body2">{v}</Typography>
            </Box>
          ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {buttons
          .filter(b => b.url)
          .map(b => (
            <Button
              key={b.title}
              size="small"
              variant="outlined"
              href={b.url!}
              target="_blank"
              rel="noopener"
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              {b.title}
            </Button>
          ))}
      </Box>
    </InfoCard>
  );
}

const conditionColumns: TableColumn<Row<'conditions'>>[] = [
  {
    title: 'Condition',
    field: 'type',
    render: c => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckIcon status={conditionStatus(c.status)} />
        {c.type}
      </Box>
    ),
  },
  { title: 'Reason', field: 'reason' },
  { title: 'Message', field: 'message' },
  {
    title: 'Since',
    field: 'lastTransitionTime',
    render: c =>
      c.lastTransitionTime
        ? `${age(c.lastTransitionTime, Date.now())} ago`
        : '',
  },
];

/**
 * Workloads are named `<namespace>-<component>` (review-13863-ds-frontend);
 * show just the component, the namespace is in the header.
 */
function shortName(name: string, namespace: string | undefined): string {
  return namespace && name.startsWith(`${namespace}-`)
    ? name.slice(namespace.length + 1)
    : name;
}

const podColumns = (
  namespace: string | undefined,
): TableColumn<Row<'pods'>>[] => [
  { title: 'Pod', field: 'name', render: p => shortName(p.name, namespace) },
  { title: 'Status', field: 'status' },
  { title: 'Ready', field: 'ready' },
  { title: 'Restarts', field: 'restarts' },
  { title: 'Age', field: 'age' },
  {
    title: '',
    render: p => (
      <>
        <ExternalLink href={p.logsUrl}>logs</ExternalLink>
        <ExternalLink href={p.consoleUrl}>console</ExternalLink>
      </>
    ),
  },
];

const deploymentColumns = (
  namespace: string | undefined,
): TableColumn<Row<'deployments'>>[] => [
  {
    title: 'Deployment',
    field: 'name',
    render: d => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckIcon status={d.ok ? 'ok' : 'pending'} />
        {shortName(d.name, namespace)}
      </Box>
    ),
  },
  { title: 'Desired', field: 'desired' },
  { title: 'Up to date', field: 'upToDate' },
  { title: 'Available', field: 'available' },
  {
    title: '',
    render: d => (
      <>
        <ExternalLink href={d.logsUrl}>logs</ExternalLink>
        <ExternalLink href={d.consoleUrl}>console</ExternalLink>
      </>
    ),
  },
];

const kustomizationColumns: TableColumn<Row<'kustomizations'>>[] = [
  {
    title: 'Flux Kustomization',
    field: 'name',
    render: k => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckIcon status={conditionStatus(k.ready ?? '')} />
        {k.name}
      </Box>
    ),
  },
  { title: 'Reason', field: 'reason' },
  { title: 'Message', field: 'message' },
  { title: 'Revision', field: 'revision' },
];

const eventColumns: TableColumn<Row<'events'>>[] = [
  {
    title: 'When',
    field: 'time',
    render: e => (e.time ? `${age(e.time, Date.now())} ago` : ''),
  },
  { title: 'Object', field: 'object' },
  { title: 'Reason', field: 'reason' },
  { title: 'Message', field: 'message' },
  { title: 'Count', field: 'count' },
];

/** Why is this one env (not) working: conditions, workloads, events, links. */
export const ReviewEnvDetailPage = () => {
  const { name = '' } = useParams();
  const { entity } = useEntity();
  const repository =
    entity.metadata.annotations?.[REVIEW_ENV_REPOSITORY_ANNOTATION] ?? '';
  const { data, error, loading } = usePolledReviewEnvs<ReviewEnvDetail>(
    `${encodeURIComponent(repository)}/${encodeURIComponent(name)}`,
  );

  if (loading) return <Progress />;
  if (error && !data) return <ResponseErrorPanel error={error} />;
  if (!data) return null;

  const failingDeployments = data.deployments.filter(d => !d.ok);
  const healthyDeployments =
    data.deployments.length - failingDeployments.length;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Header detail={data} />
      </Grid>

      {data.pods.length > 0 && (
        <Grid item xs={12}>
          <Table
            title={`Unhealthy pods (${data.pods.length})`}
            subtitle="not Running/Completed, or not all containers ready; most restarts first"
            options={{ ...DENSE, toolbar: true, maxBodyHeight: 400 }}
            columns={podColumns(data.summary.namespace)}
            data={data.pods}
          />
        </Grid>
      )}

      {data.kustomizations.length > 0 && (
        <Grid item xs={12}>
          <Table
            title="Flux"
            options={{ ...DENSE, toolbar: true }}
            columns={kustomizationColumns}
            data={data.kustomizations}
          />
        </Grid>
      )}

      <Grid item xs={12} lg={5}>
        <Table
          title="Composite conditions"
          options={{ ...DENSE, toolbar: true }}
          columns={conditionColumns}
          data={data.conditions}
        />
      </Grid>

      <Grid item xs={12} lg={7}>
        {data.deployments.length > 0 ? (
          <Table
            title="Deployments"
            subtitle={`${healthyDeployments} of ${data.deployments.length} available${
              failingDeployments.length ? ', unavailable listed first' : ''
            }`}
            options={{ ...DENSE, toolbar: true, maxBodyHeight: 400 }}
            columns={deploymentColumns(data.summary.namespace)}
            data={data.deployments}
          />
        ) : (
          <InfoCard title="Deployments">
            <Typography variant="body2">
              No Deployments in {data.summary.namespace ?? 'the env namespace'}.
              For an env whose composite only provisions infrastructure, the app
              is installed by CI afterwards — check the PR's deploy job.
            </Typography>
          </InfoCard>
        )}
      </Grid>

      <Grid item xs={12}>
        <Table
          title="Recent warning events"
          subtitle="deduplicated per object and reason, newest first"
          options={{ ...DENSE, toolbar: true }}
          columns={eventColumns}
          data={data.events}
          emptyContent={
            <Box sx={{ p: 2 }}>
              <Typography variant="body2">No warning events.</Typography>
            </Box>
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Live from the cluster as of{' '}
          {new Date(data.fetchedAt).toLocaleTimeString()}
          {error ? ' · refresh failing' : ''}
        </Typography>
      </Grid>
    </Grid>
  );
};
